<template>
  <dashboard-layout title="Partner Accounts">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search links…" />
      <button class="btn-primary" @click="showCreate = true">+ New Activation Link</button>
    </template>

    <div class="info-banner">
      <strong>How it works:</strong> Create an activation link, share it with the partner. When they activate
      their account we capture name, email, IP geolocation, ISP, browser timezone, language, and other signals
      to flag VPN/proxy or non-US visitors.
    </div>

    <stat-grid>
      <stat-card label="Activation links" :value="checks.length.toLocaleString('en-US')" icon="shield" accent />
      <stat-card label="Total clicks" :value="totalClicks.toLocaleString('en-US')" icon="activity" />
      <stat-card label="Flagged (fail)" :value="flaggedCount.toLocaleString('en-US')" icon="userx" />
    </stat-grid>

    <data-panel flush>
      <template #title>Activation Links <span class="muted">({{ filteredChecks.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="verdictFilter" :options="verdictOptions" />
      </template>

      <loading-state v-if="loading" label="Loading verification links…" />
      <data-table
        v-else
        :columns="columns"
        :rows="filteredChecks"
        row-key="id"
        clickable
        @row-click="openDetail"
      >
        <template #cell-label="{ row }">
          <div class="label">{{ row.label || '(no label)' }}</div>
          <div v-if="row.notes" class="notes">{{ row.notes }}</div>
        </template>
        <template #cell-url="{ row }">
          <div class="url-cell" @click.stop>
            <input class="url-input" :value="fullUrl(row.id)" readonly @click="$event.target.select()">
            <button class="copy-btn-sm" @click="copyToClipboard(fullUrl(row.id), 'url-' + row.id)">
              {{ copiedKey === 'url-' + row.id ? 'Copied' : 'Copy' }}
            </button>
          </div>
        </template>
        <template #cell-clicks="{ row }">
          <span class="count-badge">{{ row.clicksCount || 0 }}</span>
        </template>
        <template #cell-verdict="{ row }">
          <status-badge v-if="row.lastVerdict" :variant="verdictVariant(row.lastVerdict)" :label="row.lastVerdict" />
          <span v-else class="muted">—</span>
        </template>
        <template #cell-created="{ row }">
          <span class="nowrap">{{ formatTimestamp(row.createdAt) }}</span>
        </template>
        <template #cell-actions="{ row }">
          <button class="del-btn" title="Delete this link" @click.stop="confirmDelete(row)">×</button>
        </template>
        <template #empty>
          {{ checks.length ? 'No links match your filters' : 'No verification links yet. Create one to start.' }}
        </template>
      </data-table>
    </data-panel>

    <!-- Detail modal: clicks for one activation link -->
    <div v-if="detailCheck" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-content detail-modal">
        <div class="modal-header">
          <h2>{{ detailCheck.label || '(no label)' }}</h2>
          <button class="close-btn" @click="closeDetail">×</button>
        </div>

        <loading-state v-if="loadingClicksId === detailCheck.id" label="Loading clicks…" />
        <div v-else-if="!clicksByCheck[detailCheck.id]?.length" class="empty-clicks">
          No one has clicked this link yet.
        </div>
        <div v-else class="clicks-list">
          <div v-for="click in clicksByCheck[detailCheck.id]" :key="click.id" class="click-card">
            <div class="click-head">
              <div class="click-time">{{ formatTimestamp(click.clickedAt) }}</div>
              <span v-if="click.gpsOnly" class="gps-only-badge">GPS only</span>
              <span v-else :class="['verdict-pill', `verdict-${click.verdict || 'unknown'}`]">
                {{ (click.verdict || 'unknown').toUpperCase() }}
              </span>
            </div>

            <div v-if="click.gpsOnly" class="gps-payload">
              <div class="kv"><span class="k">GPS Lat / Lng:</span> {{ click.gpsLat?.toFixed(6) }}, {{ click.gpsLng?.toFixed(6) }}</div>
              <div class="kv"><span class="k">Accuracy:</span> {{ click.gpsAccuracy?.toFixed(0) }}m</div>
              <a class="kv k-link" :href="googleMapsLink(click.gpsLat, click.gpsLng)" target="_blank">Open in Google Maps →</a>
            </div>

            <div v-else class="click-grid">
              <div class="kv"><span class="k">IP</span><span class="v mono">{{ click.ip || '—' }}</span></div>
              <div class="kv"><span class="k">Country</span>
                <span :class="['v', click.notUS ? 'flag-bad' : 'flag-good']">
                  {{ click.countryName || click.country || '—' }}
                </span>
              </div>
              <div class="kv"><span class="k">City / Region</span><span class="v">{{ click.city || '—' }}{{ click.region ? ', ' + click.region : '' }}{{ click.postal ? ' ' + click.postal : '' }}</span></div>
              <div class="kv"><span class="k">ISP / Org</span>
                <span :class="['v', click.vpnSuspected ? 'flag-bad' : '']">{{ click.ipOrg || '—' }}</span>
              </div>
              <div class="kv"><span class="k">ASN</span><span class="v mono">{{ click.asn || '—' }}</span></div>
              <div class="kv"><span class="k">IP Timezone</span>
                <span :class="['v', click.tzMismatch ? 'flag-warn' : '']">{{ click.ipTimezone || '—' }}</span>
              </div>
              <div class="kv"><span class="k">Browser Timezone</span>
                <span :class="['v', click.tzMismatch ? 'flag-warn' : '']">{{ click.browserTimezone || '—' }}</span>
              </div>
              <div class="kv"><span class="k">Language</span>
                <span :class="['v', click.nonEnglish ? 'flag-warn' : '']">{{ click.browserLanguage || '—' }}</span>
              </div>
              <div class="kv"><span class="k">Platform</span><span class="v">{{ click.platform || '—' }}</span></div>
              <div class="kv"><span class="k">Screen</span><span class="v">{{ click.screenWidth }}×{{ click.screenHeight }} @ {{ click.pixelRatio }}x</span></div>
              <div class="kv kv-wide"><span class="k">User Agent</span><span class="v ua">{{ click.userAgent }}</span></div>
              <div v-if="click.reasons?.length" class="kv kv-wide">
                <span class="k">Reasons</span>
                <ul class="reasons">
                  <li v-for="(r, i) in click.reasons" :key="i">{{ r }}</li>
                </ul>
              </div>
              <div v-if="click.latitude && click.longitude" class="kv kv-wide">
                <span class="k">IP-based Map</span>
                <a :href="googleMapsLink(click.latitude, click.longitude)" target="_blank" class="k-link">Open in Google Maps →</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create modal -->
    <div v-if="showCreate" class="modal-overlay" @click.self="closeCreate">
      <div class="modal-content">
        <div class="modal-header">
          <h2>New Verification Link</h2>
          <button class="close-btn" @click="closeCreate">×</button>
        </div>
        <label class="form-label">Label (who is this for?)</label>
        <input
          v-model="newLabel"
          class="text-input"
          type="text"
          placeholder="e.g. Şule – Upwork partner"
          @keydown.enter="createLink"
        >
        <label class="form-label">Notes (optional)</label>
        <textarea v-model="newNotes" class="text-input" rows="3" placeholder="Any context…"></textarea>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closeCreate">Cancel</button>
          <button class="btn-primary" :disabled="creating" @click="createLink">
            {{ creating ? 'Creating…' : 'Create link' }}
          </button>
        </div>

        <div v-if="createdUrl" class="created-box">
          <div class="form-label">Share this URL:</div>
          <div class="created-row">
            <input class="url-input" :value="createdUrl" readonly @click="$event.target.select()">
            <button class="copy-btn-sm" @click="copyToClipboard(createdUrl, 'created')">
              {{ copiedKey === 'created' ? 'Copied' : 'Copy' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseVerifyService } from '../services/firebaseVerifyService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'label', label: 'Label' },
  { key: 'url', label: 'Verify URL', width: '340px' },
  { key: 'clicks', label: 'Clicks', width: '80px' },
  { key: 'verdict', label: 'Latest Verdict', width: '150px' },
  { key: 'created', label: 'Created', width: '150px' },
  { key: 'actions', label: '', width: '48px', align: 'center' }
]

export default {
  name: 'VerifyDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge
  },
  setup() {
    const service = new FirebaseVerifyService()
    const checks = ref([])
    const loading = ref(true)
    const clicksByCheck = ref({})
    const loadingClicksId = ref(null)
    const detailCheck = ref(null)
    const searchQuery = ref('')
    const verdictFilter = ref('all')
    const showCreate = ref(false)
    const creating = ref(false)
    const newLabel = ref('')
    const newNotes = ref('')
    const createdUrl = ref('')
    const copiedKey = ref(null)
    let copyResetTimer = null

    const load = async () => {
      try {
        const list = await service.listChecks()
        checks.value = list
        // Preload clicks per check so count + latest verdict are always accurate
        const preloaded = {}
        await Promise.all(list.map(async (c) => {
          try {
            preloaded[c.id] = await service.getClicks(c.id)
          } catch (e) {
            console.warn('Failed to load clicks for', c.id, e?.message || e)
            preloaded[c.id] = []
          }
        }))
        clicksByCheck.value = preloaded
        // Patch checks with derived count + lastVerdict
        checks.value = list.map(c => {
          const clicks = preloaded[c.id] || []
          const firstNonGps = clicks.find(cl => !cl.gpsOnly)
          return {
            ...c,
            clicksCount: clicks.length,
            lastVerdict: firstNonGps?.verdict || c.lastVerdict || null
          }
        })
      } catch (e) {
        console.error('Failed to load checks:', e)
      } finally {
        loading.value = false
      }
    }

    const totalClicks = computed(() => checks.value.reduce((sum, c) => sum + (c.clicksCount || 0), 0))
    const flaggedCount = computed(() => checks.value.filter(c => c.lastVerdict === 'fail').length)

    const verdictCounts = computed(() => {
      const counts = {}
      checks.value.forEach(c => {
        const v = c.lastVerdict || 'none'
        counts[v] = (counts[v] || 0) + 1
      })
      return counts
    })

    const verdictOptions = computed(() => {
      const order = ['pass', 'warn', 'fail', 'unknown', 'none']
      const labels = { pass: 'Pass', warn: 'Warn', fail: 'Fail', unknown: 'Unknown', none: 'No verdict' }
      const present = order.filter(v => verdictCounts.value[v])
      return [
        { value: 'all', label: 'All', count: checks.value.length },
        ...present.map(v => ({ value: v, label: labels[v], count: verdictCounts.value[v] }))
      ]
    })

    const filteredChecks = computed(() => {
      const q = searchQuery.value.toLowerCase().trim()
      return checks.value.filter(c => {
        if (verdictFilter.value !== 'all' && (c.lastVerdict || 'none') !== verdictFilter.value) return false
        if (q) {
          const hay = `${c.label || ''} ${c.notes || ''}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
    })

    const verdictVariant = (v) => {
      if (v === 'pass') return 'success'
      if (v === 'warn') return 'warning'
      if (v === 'fail') return 'danger'
      return 'neutral'
    }

    const fullUrl = (id) => `${window.location.origin}/account/activate/${id}`

    const formatTimestamp = (ts) => {
      if (!ts) return '—'
      const ms = ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime()
      if (isNaN(ms)) return '—'
      return new Date(ms).toLocaleString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    }

    const googleMapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`

    const loadClicks = async (checkId) => {
      if (clicksByCheck.value[checkId] !== undefined) return
      loadingClicksId.value = checkId
      try {
        const list = await service.getClicks(checkId)
        clicksByCheck.value = { ...clicksByCheck.value, [checkId]: list }
      } catch (e) {
        console.error('Failed to load clicks:', e)
        clicksByCheck.value = { ...clicksByCheck.value, [checkId]: [] }
      } finally {
        loadingClicksId.value = null
      }
    }

    const openDetail = (check) => {
      detailCheck.value = check
      // Clicks already preloaded in `load()`; only fetch if missing (failed preload)
      if (clicksByCheck.value[check.id] === undefined) {
        loadClicks(check.id)
      }
    }

    const closeDetail = () => {
      detailCheck.value = null
    }

    const closeCreate = () => {
      showCreate.value = false
      newLabel.value = ''
      newNotes.value = ''
      createdUrl.value = ''
    }

    const createLink = async () => {
      if (creating.value) return
      creating.value = true
      try {
        const id = await service.createCheck({ label: newLabel.value.trim(), notes: newNotes.value.trim() })
        createdUrl.value = fullUrl(id)
        await load()
      } catch (e) {
        console.error('Failed to create link:', e)
        window.alert('Failed to create link: ' + (e.message || 'Unknown error'))
      } finally {
        creating.value = false
      }
    }

    const confirmDelete = async (check) => {
      if (!window.confirm(`Delete verification link for "${check.label || '(no label)'}"? This cannot be undone.`)) return
      try {
        await service.deleteCheck(check.id)
        await load()
      } catch (e) {
        console.error('Delete failed:', e)
        window.alert('Delete failed: ' + (e.message || 'Unknown error'))
      }
    }

    const copyToClipboard = async (text, key) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        copiedKey.value = key
        if (copyResetTimer) clearTimeout(copyResetTimer)
        copyResetTimer = setTimeout(() => { copiedKey.value = null }, 1500)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    onMounted(load)

    return {
      checks,
      loading,
      clicksByCheck,
      loadingClicksId,
      detailCheck,
      searchQuery,
      verdictFilter,
      showCreate,
      creating,
      newLabel,
      newNotes,
      createdUrl,
      copiedKey,
      columns,
      totalClicks,
      flaggedCount,
      verdictOptions,
      filteredChecks,
      verdictVariant,
      fullUrl,
      formatTimestamp,
      googleMapsLink,
      openDetail,
      closeDetail,
      closeCreate,
      createLink,
      confirmDelete,
      copyToClipboard
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }

.btn-primary {
  padding: 8px 14px;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.btn-primary:hover { opacity: 0.92; }
.btn-secondary {
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
}
.info-banner {
  background: rgba(108, 99, 255, 0.08);
  border: 1px solid rgba(108, 99, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.55;
}
.info-banner strong { color: var(--text-primary); }

.label { font-weight: 500; color: var(--text-primary); }
.notes { font-size: 11px; color: var(--text-secondary); margin-top: 2px; }
.url-cell {
  display: flex; align-items: center; gap: 6px;
  min-width: 300px;
}
.url-input {
  flex: 1;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 11px;
  font-family: monospace;
}
.url-input:focus { outline: none; border-color: var(--primary-color); }
.copy-btn-sm {
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  color: var(--text-secondary);
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
}
.copy-btn-sm:hover { background: rgba(108, 99, 255, 0.15); color: var(--text-primary); }
.count-badge {
  display: inline-block; padding: 2px 8px; border-radius: 10px;
  background: rgba(108, 99, 255, 0.18); color: #a78bfa;
  font-size: 11px; font-weight: 700;
}
.del-btn {
  background: none; border: none;
  color: rgba(239, 68, 68, 0.5);
  font-size: 22px; line-height: 1;
  cursor: pointer; padding: 0 6px;
}
.del-btn:hover { color: #ef4444; }

.verdict-pill {
  display: inline-block; padding: 3px 10px; border-radius: 12px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
}
.verdict-pass { background: rgba(34, 197, 94, 0.15); color: #34d399; }
.verdict-fail { background: rgba(239, 68, 68, 0.15); color: #f87171; }
.verdict-warn { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }
.verdict-unknown { background: rgba(255, 255, 255, 0.06); color: var(--text-secondary); }

.empty-clicks {
  text-align: center; color: var(--text-secondary);
  padding: 16px; font-size: 12px;
}
.clicks-list { display: flex; flex-direction: column; gap: 10px; padding: 4px 0; overflow-y: auto; }
.click-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 12px 14px;
}
.click-head {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 10px;
}
.click-time {
  font-size: 12px; color: var(--text-secondary); font-family: monospace;
}
.gps-only-badge {
  font-size: 10px; padding: 3px 8px; border-radius: 6px;
  background: rgba(34, 211, 174, 0.15); color: #22d3ae;
  font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
}
.click-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px 16px;
}
.gps-payload {
  display: flex; flex-direction: column; gap: 4px;
  font-size: 13px;
}
.kv { display: flex; flex-direction: column; gap: 2px; }
.kv-wide { grid-column: 1 / -1; }
.kv .k {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.kv .v { color: var(--text-primary); font-size: 13px; word-break: break-word; }
.kv .v.mono { font-family: monospace; font-size: 12px; }
.kv .v.ua { font-family: monospace; font-size: 11px; opacity: 0.85; }
.flag-bad { color: #f87171 !important; font-weight: 600; }
.flag-warn { color: #fbbf24 !important; }
.flag-good { color: #34d399 !important; }
.k-link { color: var(--primary-color); font-size: 12px; text-decoration: none; }
.k-link:hover { text-decoration: underline; }
.reasons {
  margin: 4px 0 0; padding-left: 18px;
  font-size: 12px; color: #f87171;
}

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  display: flex; justify-content: center; align-items: center;
  z-index: 1000; padding: 20px;
}
.modal-content {
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 22px;
  width: 100%; max-width: 540px;
  display: flex; flex-direction: column; gap: 10px;
}
.modal-content.detail-modal { max-width: 760px; max-height: 85vh; }
.modal-header { display: flex; justify-content: space-between; align-items: center; }
.modal-header h2 { margin: 0; font-size: 18px; color: var(--text-primary); }
.close-btn {
  background: none; border: none; color: var(--text-secondary);
  font-size: 24px; line-height: 1; cursor: pointer; padding: 0 6px;
}
.close-btn:hover { color: var(--text-primary); }
.form-label {
  font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
  color: var(--text-secondary); margin-top: 4px;
}
.text-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 9px 12px;
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;
}
.text-input:focus { outline: none; border-color: var(--primary-color); }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 6px; }
.created-box {
  margin-top: 10px;
  padding: 12px;
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.25);
  border-radius: 8px;
  display: flex; flex-direction: column; gap: 6px;
}
.created-row { display: flex; gap: 6px; align-items: center; }
@media (max-width: 1024px) {
  .url-cell { flex-direction: column; align-items: stretch; }
}
</style>
